<template>
    <div class="space-y-6">
        <header class="mb-6">
            <Heading
                :level="1"
                title="Email Accounts"
                subtitle="Manage email accounts linked to your organization for campaign outreach."
            />
        </header>

        <Card class="rounded">
            <CardHeader>
                <CardTitle>Inbox Settings</CardTitle>
                <p class="text-sm text-muted-foreground">
                    These are the email accounts connected to your organization and their current connection status.
                </p>
            </CardHeader>

            <CardContent>
                <div v-if="emailAccounts.length > 0" class="overflow-x-auto">
                    <table class="w-full table-auto overflow-x-auto rounded border border-slate-200">
                        <caption class="sr-only">
                            List of connected email accounts and their sync status
                        </caption>
                        <thead class="rounded-sm bg-slate-50 text-xs uppercase text-slate-400">
                            <tr>
                                <th class="px-2 py-3 text-left font-semibold first:pl-5 last:pr-5">Account</th>
                                <th class="px-2 py-3 text-left font-semibold first:pl-5 last:pr-5">Signature</th>
                                <th class="w-10 px-2 py-3 first:pl-5 last:pr-5"></th>
                                <th class="px-2 py-3 text-left font-semibold first:pl-5 last:pr-5">Last Sync At</th>
                                <th class="px-2 py-3 text-left font-semibold first:pl-5 last:pr-5">Sync Status</th>
                            </tr>
                        </thead>

                        <tbody class="divide-y divide-slate-200 text-sm">
                            <tr v-for="account in emailAccounts" :key="account.id" class="hover:bg-slate-50">
                                <td class="whitespace-nowrap px-2 py-3.5 first:pl-5 last:pr-5 md:w-2/5">
                                    <div class="font-medium text-slate-900">{{ account.email_address }}</div>
                                    <div class="mt-1 text-xs text-slate-500">{{ accountOwnerName(account) }}</div>
                                </td>

                                <td class="whitespace-nowrap px-2 py-3.5 first:pl-5 last:pr-5 md:w-1/5">
                                    <Badge :variant="signatureBadgeVariant(account.signature)">
                                        {{ signatureStatusLabel(account.signature) }}
                                    </Badge>
                                </td>

                                <td class="w-10 whitespace-nowrap px-2 py-3.5 text-right first:pl-5 last:pr-5">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        class="h-8 w-8"
                                        :aria-label="signatureActionTitle(account.signature)"
                                        :title="signatureActionTitle(account.signature)"
                                        @click="openSignatureModal(account)"
                                    >
                                        <AppIcon name="pen-to-square" class="h-3.5 w-3.5" />
                                    </Button>
                                </td>

                                <td class="whitespace-nowrap px-2 py-3.5 first:pl-5 last:pr-5 md:w-1/5">
                                    <span v-if="account.last_sync_at">
                                        {{ $filters.formatDateAndTime(account.last_sync_at) }}
                                    </span>
                                    <span v-else class="text-slate-400">Never</span>
                                </td>

                                <td class="whitespace-nowrap px-2 py-3.5 first:pl-5 last:pr-5 md:w-1/5">
                                    <div
                                        v-if="state.disconnectingAccountId === account.id"
                                        class="flex items-center text-slate-500"
                                    >
                                        <AppIcon
                                            name="spinner-third"
                                            class="mr-2 h-4 w-4 animate-spin"
                                        />
                                        <span>Disconnecting...</span>
                                    </div>
                                    <Badge
                                        v-else
                                        :variant="syncStatusBadgeVariant(account.sync_status)"
                                        class="capitalize"
                                    >
                                        {{ syncStatusLabel(account.sync_status) }}
                                    </Badge>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div
                    v-else
                    class="mb-4 rounded border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500"
                >
                    No email accounts connected yet.
                </div>

                <div
                    v-if="message"
                    :class="[
                        'mt-2 rounded border py-2 text-center text-sm',
                        isEmailLinked
                            ? 'border-green-200 bg-green-100 text-green-600'
                            : isEmailFailed
                              ? 'border-red-200 bg-red-100 text-red-600'
                              : '',
                    ]"
                >
                    <p>{{ message }}</p>
                </div>
            </CardContent>
        </Card>

        <div class="flex justify-end space-x-2">
            <Button
                v-if="userHasConnectedEmail"
                variant="destructive"
                :disabled="state.disconnectingAccountId"
                @click="disconnectEmail"
            >
                Disconnect Your Email
            </Button>

            <Tooltip v-if="userHasConnectedEmail" placement="bottom" max-width="300" class="inline-block">
                <template #trigger>
                    <Button variant="default" :disabled="true">Link Email</Button>
                </template>
                <template #default>
                    <p class="text-sm">
                        You can only have 1 connected email per user at a time. Please disconnect your current email if
                        you want to add a new one.
                    </p>
                </template>
            </Tooltip>

            <Button v-else variant="default" @click="linkEmail()">Link Email</Button>
        </div>

        <EditSignatureModal
            v-if="showSignatureModal"
            :open="showSignatureModal"
            :account="selectedAccount"
            @close="showSignatureModal = false"
            @updated="onSignatureUpdated"
        />
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import emailAPI from '@/utils/api/api.email'
import Tooltip from '@/components/Tooltip.vue'
import { Heading } from '@/components/ui/typography'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import EditSignatureModal from '@/partials/modals/EditSignatureModal.vue'
import { isBlankSignatureHtml, sanitizeEmailSignature } from '@/utils/email/signatures'

export default {
    name: 'OrganizationEmailAccountsSubpage',
    components: {
        Tooltip,
        Heading,
        Card,
        CardContent,
        CardHeader,
        CardTitle,
        Button,
        Badge,
        EditSignatureModal,
    },

    data() {
        return {
            emailAccounts: [],
            state: {
                disconnectingAccountId: null,
            },
            showSignatureModal: false,
            selectedAccount: null,
        }
    },

    computed: {
        ...mapGetters(['selfActiveOrganizationId']),

        isEmailLinked() {
            return this.$route.query.status === 'success'
        },

        isEmailFailed() {
            return this.$route.query.status === 'error'
        },

        message() {
            return this.$route.query.message
        },

        userConnectedAccount() {
            const operatorId = this.$store.state.self.operator.id
            return this.emailAccounts.find((a) => a.operator.id === operatorId && a.sync_status === 'connected')
        },

        userHasConnectedEmail() {
            return Boolean(this.userConnectedAccount)
        },
    },

    created() {
        this.getEmailAccounts()
        this.checkEmailLinkStatus()
    },

    methods: {
        async getEmailAccounts() {
            const res = await emailAPI.getEmailAccounts(this.$axios)
            if (res.success) {
                this.emailAccounts = res.value ?? []
            } else {
                this.emailAccounts = []
            }
        },

        openSignatureModal(account) {
            this.selectedAccount = account
            this.showSignatureModal = true
        },

        accountOwnerName(account) {
            const firstName = account?.operator?.first_name || ''
            const lastName = account?.operator?.last_name || ''

            return `${firstName} ${lastName}`.trim() || 'Unknown owner'
        },

        safeSignature(signature) {
            return sanitizeEmailSignature(signature || '')
        },

        hasSignature(signature) {
            return !isBlankSignatureHtml(this.safeSignature(signature))
        },

        signatureBadgeVariant(signature) {
            return this.hasSignature(signature) ? 'success' : 'secondary'
        },

        signatureStatusLabel(signature) {
            return this.hasSignature(signature) ? 'Signature' : 'No signature'
        },

        signatureActionTitle(signature) {
            return this.hasSignature(signature) ? 'Edit signature' : 'Add signature'
        },

        syncStatusBadgeVariant(status) {
            switch (status) {
                case 'connected':
                    return 'success'
                case 'error':
                    return 'destructive'
                case 'disconnected':
                    return 'secondary'
                default:
                    return 'outline'
            }
        },

        syncStatusLabel(status) {
            if (!status) return 'Unknown'

            return status
                .split('_')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
        },

        async onSignatureUpdated(account) {
            if (account?.id) {
                const index = this.emailAccounts.findIndex((item) => item.id === account.id)

                if (index >= 0) {
                    this.emailAccounts.splice(index, 1, {
                        ...this.emailAccounts[index],
                        ...account,
                        signature: sanitizeEmailSignature(account.signature || ''),
                    })
                }
            }

            await this.getEmailAccounts()
            this.$successHandler('Signature updated successfully.')
        },

        async linkEmail(provider = 'gmail') {
            const res = await emailAPI.connectEmailAccount(this.$axios, provider)
            if (res.success && res.value?.authUrl) {
                window.location.href = res.value.authUrl
                return
            }

            this.$errorHandler('Unable to initiate email linking. Please try again later.', {
                title: 'Email Linking Error',
                message: res.error?.message,
            })
        },

        async disconnectEmail() {
            if (!this.userConnectedAccount) {
                this.$errorHandler('No email account found to disconnect.', {
                    title: 'Disconnect Error',
                })
                return
            }

            this.state.disconnectingAccountId = this.userConnectedAccount.id

            const res = await emailAPI.disconnectEmailAccount(this.$axios, this.userConnectedAccount.id)

            if (res.success) {
                this.$successHandler(res.data?.message || 'Disconnected successfully.')
                await this.getEmailAccounts()
            } else {
                this.$errorHandler('Unable to disconnect email account. Please try again later.', {
                    title: 'Disconnect Error',
                    message: res.error?.message,
                })
            }

            this.state.disconnectingAccountId = null
        },

        checkEmailLinkStatus() {
            const { status, message } = this.$route.query

            if (status === 'success' && message) {
                this.$successHandler(message)
                this.$router.replace({ query: {} })
            } else if (status === 'error' && message) {
                this.$errorHandler(message, { title: 'Email Linking Failed' })
                this.$router.replace({ query: {} })
            }
        },
    },
}
</script>
